import React from 'react';
import CourseRegistrationForm from './CourseRegistrationForm';

interface Course {
  id: string;
  name: string;
  price: string;
  discountPrice?: string;
  endDiscountDate?: string;
}

interface GoogleFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  course: Course;
  user: {
    name: string;
    email: string;
  };
}

const GoogleFormModal: React.FC<GoogleFormModalProps> = ({ isOpen, onClose, course, user }) => {
  if (!isOpen) return null;

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

  const handleSubmit = async (data: { name: string; email: string; courseId: string }) => {
    try {
      const response = await fetch(`${API_URL}/api/course-registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      // Try to parse JSON response
      let result;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        try {
          result = await response.json();
        } catch (parseError) {
          // If JSON parsing fails, create a generic error
          throw new Error(`Server error (${response.status}). Please try again later.`);
        }
      } else {
        // If response is not JSON, read as text
        const text = await response.text();
        throw new Error(text || `Server error (${response.status}). Please try again later.`);
      }

      if (!response.ok) {
        // Extract error message from response
        const errorMessage = result?.error || result?.message || `Server error (${response.status}). Please try again later.`;
        throw new Error(errorMessage);
      }

      return result;
    } catch (error: any) {
      // Re-throw if it's already an Error with a message
      if (error instanceof Error) {
        throw error;
      }
      // Otherwise, create a new error
      throw new Error(error?.message || 'Failed to submit registration. Please try again later.');
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      padding: '20px',
      transition: 'all 0.3s ease'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '24px',
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'hidden',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          padding: '24px 32px 0',
          backgroundColor: 'white'
        }}>
          <div style={{ display: 'flex', gap: '16px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              boxShadow: '0 4px 12px rgba(79, 70, 229, 0.2)'
            }}>
              <span style={{ fontSize: '24px' }}>📝</span>
            </div>
            <div>
              <h2 style={{
                margin: '0 0 4px 0',
                fontSize: '20px',
                fontWeight: '700',
                color: '#111827',
                letterSpacing: '-0.01em'
              }}>
                Course Registration
              </h2>
              <p style={{
                margin: 0,
                fontSize: '14px',
                color: '#6B7280',
                lineHeight: '1.5'
              }}>
                Complete the form below to secure your spot.
              </p>
            </div>
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: '1px solid #E5E7EB',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#9CA3AF',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              transition: 'all 0.2s ease',
              marginTop: '-4px',
              marginRight: '-8px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#F3F4F6';
              e.currentTarget.style.color = '#111827';
              e.currentTarget.style.borderColor = '#D1D5DB';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#9CA3AF';
              e.currentTarget.style.borderColor = '#E5E7EB';
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Form Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '0'
        }}>
          <CourseRegistrationForm
            course={course}
            user={user}
            onSubmit={handleSubmit}
            onCancel={onClose}
          />
        </div>
      </div>
    </div>
  );
};

export default GoogleFormModal;
